import React, { useState } from 'react';
import {
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Edit as EditIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axiosInstance from '../../../utils/axios';
import { useAtom } from 'jotai';
import ChildTable from './ChildrenTable';
import QuantityEditDialog from './RemediateDialog';
import { fetchInventoryData } from '../../../atoms/logistics/LogisticsManagementAtom';
import settingsAtom from '../../../atoms/settings/SettingsAtom';

const ParentRow = ({
  row,
  visibleCols,
  allColumns,
  isOpen,
  onToggle,
  data,
  setData,
  laydownList,
  current,
  setIsRefreshing,
  aliasName
}) => {
  const [loader, setLoader] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [newEntries, setNewEntries] = useState([]);
  const [editEntry, setEditEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [editType, setEditType] = useState(''); // 'remediate' or 'damaged'
  const [, setInventory] = useAtom(fetchInventoryData);
  const [settingsData] = useAtom(settingsAtom);
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));
  const timezone = currentProject?.timezone || 'Pacific/Auckland';


  const handleRowClick = async () => {
    if (!isOpen) {
      setLoader(true);
      try {
        const res = await axiosInstance(
          `/projects/inventory?bom_id=${row.bom_id}&laydown_id=${current?.id}`
        );
        if (res?.data) {
          setData(res.data);
          onToggle();
        }
      } catch (error) {
        console.error('Error loading history', error);
      } finally {
        setLoader(false);
      }
    } else {
      onToggle();
      setData({});
      setEditIndex(null);
      setEditEntry(null);
      setNewEntries([]);
    }
  };

  const handleAddEntry = () => {
    if (editIndex !== null) {
      setEditIndex(null);
      setEditEntry(null);
    }

    if (newEntries.length === 0) {
      const newEntry = {
        id: Date.now() + Math.random(),
        time: dayjs().tz(timezone),
        delivery_reference: '-',
        received_quantity: '',
        issued_quantity: '',
        purpose_code: '',
        comments: '',
      };
      setNewEntries([newEntry]);
    }
  };

const calculateDeliveredQty = (inventoryLogs) => {
  if (!inventoryLogs || !Array.isArray(inventoryLogs)) return 0;

  return inventoryLogs.reduce((total, log) => {
    if (log.purpose_code === 0) {
      // Delivered qty - ADD received_quantity
      return total + (log.received_quantity || 0);
    }
    if (log.purpose_code === 3) {
      // Intersite movement received - ADD received_quantity
      return total + (log.received_quantity || 0);
    }
    if (log.purpose_code === 2) {
      // Intersite movement issued - MINUS issued_quantity (not received_quantity)
      return total - (log.issued_quantity || 0);
    }
    return total;
  }, 0);
};
  const handleCancel = () => {
    setNewEntries([]);
    setEditIndex(null);
    setEditEntry(null);
  };

  const handleFetchData = async (row, shouldToggle = true, keepExpanded = false) => {
    setLoader(true);

    if (setIsRefreshing) setIsRefreshing(true);

    try {
      await setInventory(current.id);

      const res = await axiosInstance(
        `/projects/inventory?bom_id=${row.bom_id}&laydown_id=${current.id}`
      );
      if (res?.data) {
        setData(res.data);

        if (shouldToggle && !keepExpanded) {
          onToggle();
        }
      }
    } catch (error) {
      console.error('Error loading history', error);
    } finally {
      setLoader(false);
      if (setIsRefreshing) setIsRefreshing(false);
    }
  };

  const handleSaveAll = async () => {
    if (newEntries.length === 0) return;

    let destination_laydown_id = null;

    const entry = newEntries[0];
    let purposeCode = entry.purpose_code;

    if (typeof entry.purpose_code === 'object' && entry.purpose_code.action) {
      purposeCode = entry.purpose_code.action;
      destination_laydown_id = entry.purpose_code.destinationId;
    }

    const result = {
      comments: entry.comments,
      time: Math.floor(new Date(entry.time).getTime() / 1000),
      purpose_code: purposeCode,
      destination_laydown_id: destination_laydown_id,
      received_quantity: purposeCode === 4 ? Number(entry.issued_quantity) : 0,
      issued: purposeCode === 4 ? 0 : Number(entry.issued_quantity),
      bom_id: row?.bom_id
    };

    setLoading(true);
    try {
      const res = await axiosInstance.post(
        destination_laydown_id
          ? `/projects/inventory_update?laydown_id=${current?.id}&destination_laydown_id=${newEntries?.[0]?.purpose_code?.destinationId}`
          : `/projects/inventory_update?laydown_id=${current?.id}`,
        [result]
      );
      if (res) {
        await handleFetchData(row, false, true);
      }
    } catch (error) {
      console.error('Error saving entry:', error);
    } finally {
      setLoading(false);
      setNewEntries([]);
    }
  };

  const handleUpdate = async () => {
    let purposeCode = editEntry?.purpose_code;
    let destinationLaydownId = null;
    let subactivityId = null;
  
    if (typeof editEntry?.purpose_code === 'object' && editEntry.purpose_code.action) {
      purposeCode = editEntry.purpose_code.action;
      destinationLaydownId = editEntry.purpose_code.destinationId;
      subactivityId = editEntry.purpose_code.subactivityId;
    }
  
    const originalEntry = data.Inventory_logs[editIndex];
    
    const onlyQuantityOrCommentsChanged = (
      editEntry?.comments !== originalEntry.comments ||
      Number(editEntry?.issued_quantity) !== Number(originalEntry.issued_quantity) || Number(editEntry?.received_quantity) !== Number(originalEntry.received_quantity)
    );
  
    setLoading(true);
    
    try {
      if (onlyQuantityOrCommentsChanged) {
        let result = {
          comments: editEntry?.comments,
          time: Math.floor(new Date(editEntry?.time).getTime() / 1000),
          purpose_code: purposeCode,
          destination_laydown_id: destinationLaydownId,
          subactivity_id: subactivityId,
          received_quantity: purposeCode === 4 ? Number(editEntry?.issued_quantity) : purposeCode === 3 ? Number(editEntry?.received_quantity) : 0,
          issued: purposeCode === 4 || purposeCode === 3 ? 0 : Number(editEntry?.issued_quantity),
          bom_id: row?.bom_id,
          inventory_log_id: data.Inventory_logs[editIndex]?.inventory_log_id,
          update: true
        };
  
        const res = await axiosInstance.post(`/projects/inventory_update?laydown_id=${current?.id}`, [result]);
        if (res) {
          await handleFetchData(row, false, true);
        }
      } else {
        let deleteResult = {
          bom_id: row?.bom_id,
          delete: true,
          inventory_log_id: data.Inventory_logs[editIndex]?.inventory_log_id
        };
  
        await axiosInstance.post(`/projects/inventory_update?laydown_id=${current?.id}`, [deleteResult]);
  
        let createResult = {
          comments: editEntry?.comments,
          time: Math.floor(new Date(editEntry?.time).getTime() / 1000),
          purpose_code: purposeCode,
          destination_laydown_id: destinationLaydownId,
          subactivity_id: subactivityId,
          received_quantity: purposeCode === 4 || purposeCode === 3 ? Number(editEntry?.issued_quantity) : 0,
          issued: purposeCode === 4 || purposeCode === 3 ? 0 : Number(editEntry?.issued_quantity),
          bom_id: row?.bom_id
        };
  
        const createRes = await axiosInstance.post(
          destinationLaydownId
            ? `/projects/inventory_update?laydown_id=${current?.id}&destination_laydown_id=${destinationLaydownId}`
            : `/projects/inventory_update?laydown_id=${current?.id}`,
          [createResult]
        );
  
        if (createRes) {
          await handleFetchData(row, false, true);
        }
      }
    } catch (error) {
      console.error('Error updating entry:', error);
    } finally {
      setLoading(false);
      setEditIndex(null);
      setEditEntry(null);
    }
  };

  const handleDelete = async (item) => {
    let result = {
      bom_id: row?.bom_id,
      delete: true,
      inventory_log_id: item?.inventory_log_id
    };

    setLoading(true);
    try {
      const res = await axiosInstance.post(`/projects/inventory_update?laydown_id=${current?.id}`, [result]);
      if (res) {
        await handleFetchData(row, false, true);
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
    } finally {
      setLoading(false);
      setEditEntry(null);
    }
  };

  const handleQuantityEdit = (type) => {
    setEditType(type);
    setQuantityDialogOpen(true);
  };

  const handleDialogSuccess = async () => {
    await handleFetchData(row, false, true);
  };

  return (
    <>
      <TableRow>
        <TableCell sx={{ width: 50, padding: 0 }}>
          <IconButton size="small" onClick={handleRowClick}>
            {loader ? <CircularProgress size={18} /> : isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        {allColumns.map((col) => {
          if (!visibleCols.includes(col.key)) return null;
          let value = row[col.key];
          if (col.key === 'deliveredQty') value = calculateDeliveredQty(row.inventory_logs || data?.Inventory_logs);
          if (col.key === 'DamagedInConstruction') value = row.damaged_in_construction;
          if (col.key === 'TotalDamaged') value = row.total_damaged;
          if (col.key === 'remediateQty') value = row.remediated_quantity;
          if (col.key === 'damagedQty') value = (row.damaged);
          if (col.key === 'requiredQty') value = row.required_qty;
          if (col.key === 'distributedQty') value = row.distributed_qty;
          if (col.key === 'installedQty') value = row.installed_qty;
          if (col.key === 'currentQty') value = row.current_qty;
          if (col.key === 'DeficitQty') value = (row.required_qty - row.received_quantity);

          return (
            <TableCell
              key={col.key}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...(col.key === 'description' ? { width: '30%' } : col.key === '' ? { width: 150 } : { width: 120 }),
              }}
            >
              {col.key === 'remediateQty' ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <span>{value || 0}</span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityEdit('remediate');
                    }}
                    sx={{ padding: '2px', mb: 0.5 }}
                  >
                    <EditIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              ) : col.key === 'TotalDamaged' ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <span>{value || 0}</span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityEdit('damaged');
                    }}
                    sx={{ padding: '2px', mb: 0.5 }}
                  >
                    <EditIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              ) : (
                value
              )}
            </TableCell>
          );
        })}
      </TableRow>
      <TableRow>
        <TableCell colSpan={visibleCols.length + 1} sx={{ p: 0 }}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <ChildTable
              data={data}
              row={row}
              current={current}
              laydownList={laydownList}
              loading={loading}
              editIndex={editIndex}
              setEditIndex={setEditIndex}
              editEntry={editEntry}
              setEditEntry={setEditEntry}
              newEntries={newEntries}
              setNewEntries={setNewEntries}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onSaveAll={handleSaveAll}
              onCancel={handleCancel}
              onAddEntry={handleAddEntry}
              currentQty={row.current_qty}
            />
          </Collapse>
        </TableCell>
      </TableRow>

      <QuantityEditDialog
        open={quantityDialogOpen}
        onClose={() => setQuantityDialogOpen(false)}
        rowData={row}
        current={current}
        editType={editType}
        onSuccess={handleDialogSuccess}
      />
    </>
  );
};

export default ParentRow;